import { RedoBadge } from "@redotech/arbiter/badge/redo-badge";
import { AnimatePresence, motion } from "motion/react";
import { memo, useEffect, useRef, useState } from "react";
import { useCommandPalette } from "../../contexts/command-palette-context";
import * as styles from "./command-palette-filters.module.css";

export const CommandPaletteFilters = memo(function CommandPaletteFilters() {
  const { state, actions } = useCommandPalette();
  const visibleFilters = state.filters.filter((filter) => filter.visible);
  const hasActiveFilters = state.filters.some((filter) => filter.enabled);
  const [shouldShrink, setShouldShrink] = useState(false);
  const [naturalHeight, setNaturalHeight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Measure natural height when filters are visible
  useEffect(() => {
    if (!hasActiveFilters && containerRef.current) {
      const height = containerRef.current.scrollHeight;
      setNaturalHeight(height);
    }
  }, [hasActiveFilters, visibleFilters.length]);

  // Handle the sequence: filters exit first, then container shrinks
  useEffect(() => {
    if (hasActiveFilters) {
      // Wait for filters to exit before shrinking
      const exitDelay = visibleFilters.length * 20;
      setTimeout(() => setShouldShrink(true), exitDelay);
    } else {
      // Immediately allow expansion when filters should show
      setShouldShrink(false);
    }
  }, [hasActiveFilters, visibleFilters.length]);

  if (visibleFilters.length === 0) {
    return null;
  }

  return (
    <motion.div
      animate={{
        height: shouldShrink ? 0 : "auto",
        opacity: shouldShrink ? 0 : 1,
        paddingTop: shouldShrink ? 0 : undefined,
        paddingBottom: shouldShrink ? 0 : undefined,
        marginTop: shouldShrink ? 0 : undefined,
        marginBottom: shouldShrink ? 0 : undefined,
      }}
      className={styles.filtersContainer}
      initial={{ height: naturalHeight || "auto" }}
      ref={containerRef}
      style={{ overflow: shouldShrink ? "hidden" : "visible" }}
      transition={{ duration: 0.2, ease: "easeInOut" }}
    >
      <AnimatePresence>
        {!hasActiveFilters &&
          visibleFilters.map((filter, index) => (
            <motion.div
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{
                opacity: 0,
                x: -8,
                scale: 0.9,
                transition: { duration: 0.1, delay: index * 0.03 },
              }}
              initial={{ opacity: 0, x: -8, scale: 0.9 }}
              key={filter.id}
              style={{ display: "inline-block" }}
              transition={{
                duration: 0.12,
                delay: index * 0.02,
                type: "spring",
                bounce: 0.2,
              }}
            >
              <button
                className={styles.filterButton}
                onClick={() => {
                  actions.toggleFilter(filter.id);
                  actions.focusSearchInput();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    actions.toggleFilter(filter.id);
                    actions.focusSearchInput();
                  }
                }}
                type="button"
              >
                <RedoBadge
                  color="gray"
                  size="sm"
                  text={filter.label}
                  type={filter.enabled ? "alternate" : "primary"}
                />
              </button>
            </motion.div>
          ))}
      </AnimatePresence>
    </motion.div>
  );
});
